import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { WifiHigh, WifiSlash, ThermometerHot, Drop, Wind, CloudRain, Sun, Plant, ArrowsLeftRight, Plugs, Trash, ArrowSquareOut } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

export const WeatherStation = () => {
  const [status, setStatus] = useState(null);
  const [stationData, setStationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [showDisconnect, setShowDisconnect] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/weather/station/status`, { withCredentials: true });
      setStatus(res.data);
      if (res.data.connected) {
        fetchStationData();
      }
    } catch {
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStationData = async () => {
    try {
      setDataLoading(true);
      const res = await axios.get(`${API_URL}/api/weather/station/data`, { withCredentials: true });
      setStationData(res.data);
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Station data error:', err);
      }
    } finally {
      setDataLoading(false);
    }
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    if (!apiKey.trim()) return;
    try {
      setConnecting(true);
      const res = await axios.post(`${API_URL}/api/weather/station/connect`, { api_key: apiKey }, { withCredentials: true });
      toast.success(res.data.message);
      setShowConnect(false);
      setApiKey('');
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect station');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.delete(`${API_URL}/api/weather/station/disconnect`, { withCredentials: true });
      toast.success('Weather station disconnected');
      setStatus({ connected: false });
      setStationData(null);
      setShowDisconnect(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disconnect');
    }
  };

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  if (loading) return null;

  // Not connected — show connect card
  if (!status?.connected) {
    return (
      <div data-testid="weather-station-section">
        <div className="flex items-center gap-2 mb-3">
          <Plugs size={18} weight="duotone" className="text-primary" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Weather Station</h2>
        </div>
        <Card data-testid="station-connect-card" className="border-dashed border-2 border-muted-foreground/20">
          <CardContent className="pt-6 pb-5 text-center">
            <WifiSlash size={36} className="mx-auto text-muted-foreground/40 mb-3" />
            <p className="font-semibold text-sm mb-1">Connect Your Weather Station</p>
            <p className="text-xs text-muted-foreground mb-4 max-w-sm mx-auto">
              Get hyperlocal data from your backyard — not the nearest airport. Supports Ambient Weather stations.
            </p>
            <Button data-testid="station-connect-btn" size="sm" onClick={() => setShowConnect(true)}>
              <Plugs size={14} className="mr-1.5" /> Connect Station
            </Button>
          </CardContent>
        </Card>

        <Dialog open={showConnect} onOpenChange={setShowConnect}>
          <DialogContent data-testid="station-connect-dialog">
            <DialogHeader>
              <DialogTitle>Connect Ambient Weather Station</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleConnect} className="space-y-4 mt-2">
              <div>
                <Label htmlFor="ambient-api-key">Your Ambient Weather API Key</Label>
                <Input
                  id="ambient-api-key"
                  data-testid="station-api-key-input"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Paste your API key here"
                  required
                  className="mt-1.5 font-mono text-xs"
                />
                <p className="text-[10px] text-muted-foreground mt-1.5">
                  Find your key at{' '}
                  <a href="https://ambientweather.net/account" target="_blank" rel="noopener noreferrer" className="text-primary underline inline-flex items-center gap-0.5">
                    ambientweather.net/account <ArrowSquareOut size={10} />
                  </a>
                </p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground space-y-1">
                <p className="font-medium text-foreground text-xs">How to get your API key:</p>
                <ol className="list-decimal list-inside space-y-0.5">
                  <li>Sign in at ambientweather.net</li>
                  <li>Go to Account Settings</li>
                  <li>Click "API Keys" and create one</li>
                  <li>Copy the key and paste it above</li>
                </ol>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowConnect(false)}>Cancel</Button>
                <Button type="submit" data-testid="station-submit-btn" disabled={connecting}>
                  {connecting ? 'Connecting...' : 'Connect'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Connected — show live data
  const s = stationData?.station;
  const cmp = stationData?.comparison;

  return (
    <div data-testid="weather-station-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <WifiHigh size={18} weight="duotone" className="text-green-500" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {s?.station_name || status.station_name || 'Weather Station'}
          </h2>
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" title="Live" />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="text-xs h-7" onClick={fetchStationData} disabled={dataLoading}>
            {dataLoading ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button variant="ghost" size="sm" className="text-xs h-7 text-red-500 hover:text-red-700" onClick={() => setShowDisconnect(true)}>
            <Trash size={12} />
          </Button>
        </div>
      </div>

      {!s ? (
        <Card className="animate-pulse"><CardContent className="pt-5 pb-4 h-40" /></Card>
      ) : (
        <>
          {/* Live Readings Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
            {/* Temperature */}
            <Card data-testid="station-temp" className="bg-orange-50 dark:bg-orange-950/20">
              <CardContent className="pt-4 pb-3 text-center">
                <ThermometerHot size={20} className="mx-auto text-orange-500 mb-1" />
                <p className="text-2xl font-bold">{s.outdoor.temp_f != null ? `${Math.round(s.outdoor.temp_f)}°` : '—'}</p>
                <p className="text-[9px] text-muted-foreground">Temperature</p>
                {s.outdoor.feels_like_f != null && s.outdoor.feels_like_f !== s.outdoor.temp_f && (
                  <p className="text-[9px] text-muted-foreground">Feels {Math.round(s.outdoor.feels_like_f)}°</p>
                )}
              </CardContent>
            </Card>

            {/* Humidity */}
            <Card data-testid="station-humidity" className="bg-teal-50 dark:bg-teal-950/20">
              <CardContent className="pt-4 pb-3 text-center">
                <Drop size={20} className="mx-auto text-teal-500 mb-1" />
                <p className="text-2xl font-bold">{s.outdoor.humidity != null ? `${s.outdoor.humidity}%` : '—'}</p>
                <p className="text-[9px] text-muted-foreground">Humidity</p>
                {s.outdoor.dew_point_f != null && (
                  <p className="text-[9px] text-muted-foreground">Dew {Math.round(s.outdoor.dew_point_f)}°</p>
                )}
              </CardContent>
            </Card>

            {/* Wind */}
            <Card data-testid="station-wind" className="bg-sky-50 dark:bg-sky-950/20">
              <CardContent className="pt-4 pb-3 text-center">
                <Wind size={20} className="mx-auto text-sky-500 mb-1" />
                <p className="text-2xl font-bold">{s.wind.speed_mph != null ? s.wind.speed_mph : '—'}<span className="text-xs font-normal">mph</span></p>
                <p className="text-[9px] text-muted-foreground">{s.wind.direction || ''} Wind</p>
                {s.wind.gust_mph != null && s.wind.gust_mph > 0 && (
                  <p className="text-[9px] text-muted-foreground">Gusts {s.wind.gust_mph}mph</p>
                )}
              </CardContent>
            </Card>

            {/* Rain */}
            <Card data-testid="station-rain" className="bg-blue-50 dark:bg-blue-950/20">
              <CardContent className="pt-4 pb-3 text-center">
                <CloudRain size={20} className="mx-auto text-blue-500 mb-1" />
                <p className="text-2xl font-bold">{s.rain.daily_in != null ? `${s.rain.daily_in}"` : '—'}</p>
                <p className="text-[9px] text-muted-foreground">Rain Today</p>
                {s.rain.weekly_in != null && (
                  <p className="text-[9px] text-muted-foreground">Week: {s.rain.weekly_in}"</p>
                )}
              </CardContent>
            </Card>

            {/* UV */}
            <Card data-testid="station-uv" className="bg-amber-50 dark:bg-amber-950/20">
              <CardContent className="pt-4 pb-3 text-center">
                <Sun size={20} className="mx-auto text-amber-500 mb-1" />
                <p className="text-2xl font-bold">{s.solar.uv_index != null ? s.solar.uv_index : '—'}</p>
                <p className="text-[9px] text-muted-foreground">UV Index</p>
                {s.solar.solar_radiation != null && (
                  <p className="text-[9px] text-muted-foreground">{s.solar.solar_radiation} W/m²</p>
                )}
              </CardContent>
            </Card>

            {/* Soil (if available) */}
            {s.soil && Object.keys(s.soil).length > 0 ? (
              <Card data-testid="station-soil" className="bg-emerald-50 dark:bg-emerald-950/20">
                <CardContent className="pt-4 pb-3 text-center">
                  <Plant size={20} className="mx-auto text-emerald-600 mb-1" />
                  {Object.entries(s.soil).slice(0, 1).map(([key, val]) => (
                    <div key={key}>
                      <p className="text-2xl font-bold">{val.temp_f != null ? `${Math.round(val.temp_f)}°` : '—'}</p>
                      <p className="text-[9px] text-muted-foreground">Soil Temp</p>
                      {val.moisture != null && <p className="text-[9px] text-muted-foreground">Moisture: {val.moisture}%</p>}
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : (
              <Card data-testid="station-pressure" className="bg-violet-50 dark:bg-violet-950/20">
                <CardContent className="pt-4 pb-3 text-center">
                  <ArrowsLeftRight size={20} className="mx-auto text-violet-500 mb-1" />
                  <p className="text-2xl font-bold">{s.pressure.relative_inhg != null ? s.pressure.relative_inhg.toFixed(2) : '—'}</p>
                  <p className="text-[9px] text-muted-foreground">Pressure (inHg)</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Microclimate Comparison */}
          {cmp && (
            <Card data-testid="station-comparison-card" className="border-primary/20">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 mb-3">
                  <ArrowsLeftRight size={16} weight="duotone" className="text-primary" />
                  <span className="font-semibold text-xs">Your Yard vs Nearest Weather Grid</span>
                </div>
                <div className="grid grid-cols-3 gap-4 mb-3">
                  {/* Temp comparison */}
                  <div className="text-center">
                    <p className="text-[10px] font-semibold uppercase text-muted-foreground mb-1">Temp</p>
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-sm font-bold">{cmp.temp_f.station ?? '—'}°</span>
                      <span className="text-[10px] text-muted-foreground">vs</span>
                      <span className="text-sm text-muted-foreground">{cmp.temp_f.api}°</span>
                    </div>
                    {cmp.temp_f.delta != null && (
                      <p className={`text-[10px] font-medium ${
                        Math.abs(cmp.temp_f.delta) >= 3 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'
                      }`}>
                        {cmp.temp_f.delta > 0 ? '+' : ''}{cmp.temp_f.delta}°F
                      </p>
                    )}
                  </div>
                  {/* Humidity comparison */}
                  <div className="text-center">
                    <p className="text-[10px] font-semibold uppercase text-muted-foreground mb-1">Humidity</p>
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-sm font-bold">{cmp.humidity.station ?? '—'}%</span>
                      <span className="text-[10px] text-muted-foreground">vs</span>
                      <span className="text-sm text-muted-foreground">{cmp.humidity.api}%</span>
                    </div>
                    {cmp.humidity.delta != null && (
                      <p className={`text-[10px] font-medium ${
                        Math.abs(cmp.humidity.delta) >= 10 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'
                      }`}>
                        {cmp.humidity.delta > 0 ? '+' : ''}{cmp.humidity.delta}%
                      </p>
                    )}
                  </div>
                  {/* Wind comparison */}
                  <div className="text-center">
                    <p className="text-[10px] font-semibold uppercase text-muted-foreground mb-1">Wind</p>
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-sm font-bold">{cmp.wind_mph.station ?? '—'}</span>
                      <span className="text-[10px] text-muted-foreground">vs</span>
                      <span className="text-sm text-muted-foreground">{cmp.wind_mph.api}mph</span>
                    </div>
                    {cmp.wind_mph.delta != null && (
                      <p className={`text-[10px] font-medium ${
                        Math.abs(cmp.wind_mph.delta) >= 3 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'
                      }`}>
                        {cmp.wind_mph.delta > 0 ? '+' : ''}{cmp.wind_mph.delta}mph
                      </p>
                    )}
                  </div>
                </div>
                {/* Insights */}
                <div className="space-y-1">
                  {cmp.insight.map((i, idx) => (
                    <p key={idx} className="text-[11px] text-muted-foreground">{i}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Disconnect Confirmation */}
      <Dialog open={showDisconnect} onOpenChange={setShowDisconnect}>
        <DialogContent data-testid="station-disconnect-dialog">
          <DialogHeader><DialogTitle>Disconnect Weather Station?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This will remove your station connection. You can reconnect anytime with your API key.</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDisconnect(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDisconnect} data-testid="station-confirm-disconnect">Disconnect</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
